import type { DeviceCategory } from '@mvcp/contracts';
import { Icon, type IconName } from './Icon';
export { DEVICE_CATEGORY_OPTIONS } from '../lib/device-category';


/**
 * Category → glyph. A `Record` over the closed {@link DeviceCategory} union, so adding a
 * category to `DEVICE_CATEGORIES` is a compile error here until it has been given a shape —
 * which is the whole reason the column stopped being free text.
 *
 * `Automation` reuses `cpu` and `Other` reuses `package` (both already in the registry for
 * the sidebar); the remaining five are new. `Sonar` and `Communication` are deliberately
 * different silhouettes rather than two variations on radiating arcs: they sit next to each
 * other in the list, and at 16px a near-duplicate glyph is worse than no glyph, because it
 * asserts a distinction the reader then cannot make.
 */
const CATEGORY_ICON: Record<DeviceCategory, IconName> = {
  Navigation: 'compass',
  Communication: 'radio',
  Automation: 'cpu',
  Propulsion: 'zap',
  Safety: 'life-buoy',
  Sonar: 'waves',
  Other: 'package',
};

/**
 * Deterministic category → avatar hue.
 *
 * The tile borrows `Avatar`'s palette rather than introducing a second one, but indexes it by
 * POSITION in `DEVICE_CATEGORIES` instead of by hashing an id. The difference matters: an
 * avatar hue distinguishes one subject from the next and only has to be stable per subject,
 * whereas a category colour is a shared key — every Navigation device in the fleet must be
 * the same colour, or the colour says nothing. A hash of the category name would deliver
 * that too, but it would also let a rename silently re-colour the whole class, and it would
 * let two categories collide on one hue with no way to see it coming.
 *
 * NOT A STATUS COLOUR. Same rule as `Avatar`: these ten hues encode identity, never health.
 * A red Propulsion tile beside a green "Verified" pill must not read as a contradiction, so
 * the palette is deliberately the identity one and not the five-tone status triad.
 */
const CATEGORY_HUE: Record<DeviceCategory, number> = {
  Navigation: 1,
  Communication: 2,
  Automation: 3,
  Propulsion: 4,
  Safety: 5,
  Sonar: 6,
  Other: 7,
};

/**
 * The equipment-category tile shown on a Device row, in `Avatar`'s place.
 *
 * A monogram of the MODEL was the wrong tile for a device list. It answered "which row is
 * this" with two letters the row already spells out in full immediately beside it, and it
 * spent the one pre-attentive slot in the row — a coloured shape — on information that was
 * already there in text. The category is not otherwise visible at a glance, and it is what a
 * reader scanning a fleet actually groups by, so it earns the slot.
 *
 * **Absent category renders nothing at all.** Not a placeholder tile, not `Other`: `Other`
 * means "recorded, but outside our list" and a missing category means nobody has said yet.
 * Drawing the `Other` glyph for both would collapse a distinction the schema keeps
 * (`039_device_category_enum.sql`), and inventing a neutral tile just to hold the column's
 * width would put a shape on the row that means nothing.
 *
 * `aria-hidden` unless `label` is passed, for `Avatar`'s reason: on every current call site
 * the category is also rendered as text in its own cell, and announcing the glyph too would
 * make the row read its category twice.
 */
export function DeviceCategoryIcon({
  category,
  label,
}: {
  /** The device's category. `undefined` renders nothing (see above). */
  category?: DeviceCategory;
  /** Accessible name. Omit when the category is also present as text in the same row. */
  label?: string;
}): React.JSX.Element | null {
  if (category === undefined) return null;
  return (
    <span
      className="avatar"
      data-hue={CATEGORY_HUE[category]}
      data-category={category}
      {...(label === undefined ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      <Icon name={CATEGORY_ICON[category]} size={16} />
    </span>
  );
}
